import React, { useState, useMemo, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Search, Plus, UserPlus, Send } from 'lucide-react'
import toast from 'react-hot-toast'
import { showSuccess, showError } from '@/lib/swal'
import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { useAuthStore } from '@/store/auth.store'
import { attendanceService } from '@/services/attendanceService'
import { employeesService } from '@/services/employeesService'
import { shiftService } from '@/services/shiftService'
import { calendarFromApi } from '@/lib/calendarDate'
import { collapseDuplicatedFullName, formatEmployeeDisplayName } from '@/lib/employeeName'
import type { AttendanceListRecord } from '@/api/types'
import type { Employee } from '@/api/types'
import type { DepartmentLookup } from '@/api/types'
import type { ShiftListItem } from '@/api/types'

function formatAttendanceDate(iso?: string | null, locale?: string): string {
  const day = calendarFromApi(iso)
  if (!day) return '—'
  const loc = locale?.toLowerCase().startsWith('ar') ? 'ar-SA' : 'en-US'
  return new Date(`${day}T12:00:00`).toLocaleDateString(loc, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

/** Convert local date + time to UTC ISO string for PostgreSQL timestamp with time zone. */
function toUTCISO(dateStr: string, timeHHmm: string): string {
  if (!dateStr || !timeHHmm || timeHHmm.length < 5) return ''
  const local = `${dateStr}T${timeHHmm}:00`
  return new Date(local).toISOString()
}

/** For input type="time" (HH:mm). */
function isoToTimeInput(iso?: string | null): string {
  if (!iso) return ''
  try {
    const d = new Date(iso)
    const h = d.getHours()
    const m = d.getMinutes()
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
  } catch {
    return ''
  }
}

function formatTimeDisplay(iso?: string | null, locale?: string): string {
  if (!iso) return '—'
  try {
    const loc = locale?.toLowerCase().startsWith('ar') ? 'ar-SA' : 'en-US'
    const d = new Date(iso)
    return d.toLocaleTimeString(loc, { hour: '2-digit', minute: '2-digit', hour12: true })
  } catch {
    return String(iso).slice(11, 16) || '—'
  }
}

const DEFAULT_SHIFT_START = '09:00'
const DEFAULT_SHIFT_HOURS = 8

function shiftTimeToHHmm(value?: string | null): string {
  if (!value || typeof value !== 'string') return ''
  const trimmed = value.trim()
  if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(trimmed)) return trimmed.slice(0, 5)
  try {
    const d = new Date(trimmed)
    if (!Number.isNaN(d.getTime())) {
      return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
    }
  } catch {
    return ''
  }
  return ''
}

function hoursBetween(startHHmm: string, endHHmm: string): number {
  const [sh, sm] = startHHmm.split(':').map(Number)
  const [eh, em] = endHHmm.split(':').map(Number)
  let mins = eh * 60 + em - (sh * 60 + sm)
  if (mins <= 0) mins += 24 * 60
  return mins / 60
}

/** Calculate totalAttendedHours, lateHoursCount, incompleteHours from Time In/Out and the employee shift. */
function calculateAttendanceMetrics(
  dateStr: string,
  timeInStr: string,
  timeOutStr: string,
  expectedWorkingHours: number,
  shiftStartHHmm: string
): { totalAttendedHours: number; lateHoursCount: number; incompleteHours: number } {
  if (!dateStr || !timeInStr || timeInStr.length < 5 || !timeOutStr || timeOutStr.length < 5) {
    return { totalAttendedHours: 0, lateHoursCount: 0, incompleteHours: expectedWorkingHours }
  }
  const dIn = new Date(`${dateStr}T${timeInStr}:00`)
  const dOut = new Date(`${dateStr}T${timeOutStr}:00`)
  if (dOut.getTime() < dIn.getTime()) dOut.setDate(dOut.getDate() + 1)
  const expectedStart = new Date(`${dateStr}T${shiftStartHHmm}:00`)
  const actualAttendedHours = Math.max(0, (dOut.getTime() - dIn.getTime()) / 3600000)
  const lateMinutes = dIn > expectedStart ? (dIn.getTime() - expectedStart.getTime()) / 60000 : 0
  const lateHoursCount = Math.round((lateMinutes / 60) * 100) / 100
  const incompleteHours = Math.max(0, Math.round(expectedWorkingHours - actualAttendedHours))
  return {
    totalAttendedHours: Math.round(actualAttendedHours * 10) / 10,
    lateHoursCount,
    incompleteHours,
  }
}

function mapToListRecord(r: any): AttendanceListRecord {
  return {
    id: r.id ?? 0,
    empId: r.empId ?? r.employeeId ?? 0,
    attendanceDate: r.attendanceDate ?? undefined,
    employeeCode: r.employeeCode ?? r.empCode ?? '',
    employeeName: collapseDuplicatedFullName(r.employeeName ?? r.empName ?? ''),
    timeIn: r.timeIn ?? null,
    timeOut: r.timeOut ?? null,
    isPresent: r.isPresent ?? 1,
    totalAttendedHours: r.totalAttendedHours ?? 0,
    lateHoursCount: r.lateHoursCount ?? 0,
    incompleteHours: r.incompleteHours ?? r.uncompleteHours ?? 0,
    isPosted: r.isPosted ?? false,
  }
}

function getDefaultDateRange(): { dateFrom: string; dateTo: string } {
  const now = new Date()
  const y = now.getFullYear()
  const m = String(now.getMonth() + 1).padStart(2, '0')
  const d = String(now.getDate()).padStart(2, '0')
  return {
    dateFrom: `${y}-${m}-01`,
    dateTo: `${y}-${m}-${d}`,
  }
}

export const Attendance: React.FC = () => {
  const { t, i18n } = useTranslation('common')
  const locale = i18n.language
  const navigate = useNavigate()
  const { user, token } = useAuthStore((s) => ({ user: s.user, token: s.token }))
  const branchId = user?.selectedBranch?.branchId

  const [loading, setLoading] = useState(false)
  const [rows, setRows] = useState<AttendanceListRecord[]>([])
  const [search, setSearch] = useState('')
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(50)
  const [totalRecords, setTotalRecords] = useState(0)
  const [edits, setEdits] = useState<Record<number, { timeIn?: string; timeOut?: string; isPresent?: number }>>({})
  const [updatingId, setUpdatingId] = useState<number | null>(null)
  const [dateFrom, setDateFrom] = useState(() => getDefaultDateRange().dateFrom)
  const [dateTo, setDateTo] = useState(() => getDefaultDateRange().dateTo)
  const [departmentId, setDepartmentId] = useState(0)
  const [empId, setEmpId] = useState(0)
  const [departments, setDepartments] = useState<DepartmentLookup[]>([])
  const [employeesList, setEmployeesList] = useState<Employee[]>([])
  const [shifts, setShifts] = useState<ShiftListItem[]>([])
  const [posting, setPosting] = useState(false)

  const loadData = async () => {
    if (!branchId) return
    setLoading(true)
    try {
      const res = await attendanceService.getListByFilters(branchId, {
        departmentId: departmentId || 0,
        empId: empId || 0,
        dateFrom,
        dateTo,
        pageNumber,
        pageSize,
      })
      setRows((res.data || []).map(mapToListRecord))
      setTotalRecords(res.totalRecords ?? 0)
      setEdits({})
    } catch (e: any) {
      const status = e?.response?.status
      const msg = (e?.response?.data?.message || '').toLowerCase()
      const isNoData = status === 404 || /not found|no data|no records|no attendance/.test(msg)
      setRows([])
      setTotalRecords(0)
      if (!isNoData) {
        toast.error(e?.response?.data?.message ?? t('attendancePage.fetchFailed'))
      }
    } finally {
      setLoading(false)
    }
  }

  const getRowTimeIn = (row: AttendanceListRecord) =>
    edits[row.id]?.timeIn ?? isoToTimeInput(row.timeIn)
  const getRowTimeOut = (row: AttendanceListRecord) =>
    edits[row.id]?.timeOut ?? isoToTimeInput(row.timeOut)
  const getRowIsPresent = (row: AttendanceListRecord) =>
    edits[row.id]?.isPresent ?? row.isPresent

  const getRowShift = (row: AttendanceListRecord) => {
    const emp = (
      row.empId
        ? employeesList.find((e) => e.id === row.empId)
        : employeesList.find((e) => e.empCode === row.employeeCode)
    ) as (Employee & { shiftId?: number }) | undefined
    const shift = shifts.find((s) => s.id === emp?.shiftId)
    const start =
      shiftTimeToHHmm(shift?.shiftStartTime ?? shift?.startTime) || DEFAULT_SHIFT_START
    const end = shiftTimeToHHmm(shift?.shiftEndTime ?? shift?.endTime)
    const hours = end ? hoursBetween(start, end) : DEFAULT_SHIFT_HOURS
    return { start, hours: hours > 0 ? hours : DEFAULT_SHIFT_HOURS }
  }

  const setTimeIn = (id: number, value: string) => {
    setEdits((prev) => ({ ...prev, [id]: { ...prev[id], timeIn: value || undefined } }))
  }
  const setTimeOut = (id: number, value: string) => {
    setEdits((prev) => ({ ...prev, [id]: { ...prev[id], timeOut: value || undefined } }))
  }
  const setIsPresent = (id: number, value: number) => {
    setEdits((prev) => ({ ...prev, [id]: { ...prev[id], isPresent: value } }))
  }

  const getRowMetrics = (row: AttendanceListRecord) => {
    const dateStr = calendarFromApi(row.attendanceDate)
    const timeInStr = getRowTimeIn(row)
    const timeOutStr = getRowTimeOut(row)
    const shift = getRowShift(row)
    if (!dateStr || !timeInStr || timeOutStr.length < 5) {
      return {
        totalAttendedHours: row.totalAttendedHours ?? 0,
        lateHoursCount: row.lateHoursCount ?? 0,
        incompleteHours: row.incompleteHours ?? 0,
      }
    }
    return calculateAttendanceMetrics(dateStr, timeInStr, timeOutStr, shift.hours, shift.start)
  }

  const handleUpdateRow = async (row: AttendanceListRecord) => {
    const timeInStr = getRowTimeIn(row)
    const timeOutStr = getRowTimeOut(row)
    const dateStr = calendarFromApi(row.attendanceDate) || new Date().toISOString().slice(0, 10)
    const shift = getRowShift(row)
    const metrics = calculateAttendanceMetrics(dateStr, timeInStr, timeOutStr, shift.hours, shift.start)
    const timeIn = timeInStr
      ? toUTCISO(dateStr, timeInStr)
      : (row.timeIn ? new Date(row.timeIn).toISOString() : '')
    const timeOut = timeOutStr
      ? toUTCISO(dateStr, timeOutStr)
      : (row.timeOut ? new Date(row.timeOut).toISOString() : '')
    const isPresent = getRowIsPresent(row) === 1 ? 1 : 0
    const payload = {
      attendanceData: [
        {
          id: row.id,
          timeIn,
          timeOut,
          isPresent,
          totalAttendedHours: isPresent === 0 ? 0 : metrics.totalAttendedHours,
          lateHoursCount: isPresent === 0 ? 0 : metrics.lateHoursCount,
          incompleteHours: isPresent === 0 ? 0 : metrics.incompleteHours,
        },
      ],
    }
    console.log('[Attendance Update] PUT /api/Attendance/Update — request body:', JSON.stringify(payload, null, 2))
    setUpdatingId(row.id)
    try {
      await attendanceService.update(payload)
      await showSuccess(t('attendancePage.updateSuccessTitle'), t('attendancePage.updateSuccessMessage'))
      setEdits((prev) => {
        const next = { ...prev }
        delete next[row.id]
        return next
      })
      loadData()
    } catch (e: any) {
      await showError('Update failed', e?.response?.data?.message ?? e?.message ?? 'Failed to update.')
    } finally {
      setUpdatingId(null)
    }
  }

  useEffect(() => {
    setPageNumber(1)
  }, [branchId])

  useEffect(() => {
    if (!branchId) {
      setRows([])
      setTotalRecords(0)
      return
    }
    if (!token) return
    loadData()
  }, [branchId, token, pageNumber, pageSize, dateFrom, dateTo, departmentId, empId])

  useEffect(() => {
    if (!branchId) {
      setDepartments([])
      return
    }
    let cancelled = false
    employeesService.getDepartmentLookups().then((list) => {
      if (!cancelled && list) setDepartments(list)
    }).catch(() => { if (!cancelled) setDepartments([]) })
    return () => { cancelled = true }
  }, [branchId])

  useEffect(() => {
    if (!branchId) {
      setEmployeesList([])
      return
    }
    let cancelled = false
    employeesService.getAllEmployees(branchId, 1, 500).then((res) => {
      if (!cancelled && res?.data) setEmployeesList(res.data)
    }).catch(() => { if (!cancelled) setEmployeesList([]) })
    return () => { cancelled = true }
  }, [branchId])

  useEffect(() => {
    if (!branchId) {
      setShifts([])
      return
    }
    let cancelled = false
    shiftService.getAll(branchId, 1, 500).then((res) => {
      if (!cancelled && res?.data) setShifts(res.data)
    }).catch(() => { if (!cancelled) setShifts([]) })
    return () => { cancelled = true }
  }, [branchId])

  const employeeOptionsByDepartment = useMemo(() => {
    const seen = new Set<number>()
    const nameFor = (emp: Employee) =>
      formatEmployeeDisplayName(emp) || emp.empCode || '—'
    const toOption = (emp: Employee) => {
      if (!emp.id || !emp.empCode || seen.has(emp.id)) return null
      seen.add(emp.id)
      return { value: String(emp.id), label: nameFor(emp) }
    }
    const source = !departmentId
      ? employeesList
      : employeesList.filter((emp) => emp.empDepartmetId === departmentId)
    return source.map(toOption).filter((o): o is { value: string; label: string } => o != null)
  }, [employeesList, departmentId])

  const filteredRows = useMemo(() => {
    let list = rows
    if (search.trim()) {
      const q = search.toLowerCase()
      list = list.filter(
        (r) =>
          (r.employeeCode ?? '').toLowerCase().includes(q) ||
          (r.employeeName ?? '').toLowerCase().includes(q)
      )
    }
    return [...list].sort((a, b) => {
      const da = calendarFromApi(a.attendanceDate) || a.attendanceDate || ''
      const db = calendarFromApi(b.attendanceDate) || b.attendanceDate || ''
      if (da !== db) return da.localeCompare(db)
      const empA = (a.employeeCode ?? '') + (a.employeeName ?? '')
      const empB = (b.employeeCode ?? '') + (b.employeeName ?? '')
      return empA.localeCompare(empB)
    })
  }, [rows, search])

  const handlePostAttendance = async () => {
    if (!branchId) {
      toast.error(t('attendancePage.postSelectBranch'))
      return
    }
    setPosting(true)
    try {
      await attendanceService.postAttendance(dateFrom, dateTo, departmentId || 0, branchId)
      await showSuccess(t('attendancePage.postSuccessTitle'), t('attendancePage.postSuccessMessage'))
      loadData()
    } catch (e: any) {
      await showError(t('attendancePage.postFailedTitle'), e?.response?.data?.message ?? e?.message ?? t('attendancePage.postFailedMessage'))
    } finally {
      setPosting(false)
    }
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <PageHeader
        title={t('attendancePage.pageTitle')}
        description={t('attendancePage.listDescription')}
      >
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="default"
            onClick={() => navigate('/payroll/attendance/combined/new')}
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('attendancePage.addCombined')}
          </Button>
          <Button
            variant="default"
            onClick={() => navigate('/payroll/attendance/manual/new')}
          >
            <UserPlus className="w-4 h-4 mr-2" />
            {t('attendancePage.addManual')}
          </Button>
        </div>
      </PageHeader>

      <Card>
        <CardHeader>
          <CardTitle>{t('attendancePage.cardTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">{t('attendancePage.dateFrom')}</label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{t('attendancePage.dateTo')}</label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{t('attendancePage.department')}</label>
              <SearchableSelect
                options={[
                  { value: '0', label: t('attendancePage.allDepartments') },
                  ...departments.filter((d) => d.id).map((d) => ({
                    value: String(d.id),
                    label: d.lookupNameEn || d.lookupNameAr || String(d.id),
                  })),
                ]}
                value={departmentId ? String(departmentId) : '0'}
                onValueChange={(v) => {
                  setDepartmentId(v ? Number(v) : 0)
                  setEmpId(0)
                }}
                placeholder={t('attendancePage.allDepartments')}
                searchPlaceholder={t('attendancePage.searchDepartment')}
                emptyMessage={t('attendancePage.emptyDepartment')}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{t('attendancePage.employee')}</label>
              <SearchableSelect
                options={[
                  { value: '0', label: t('attendancePage.allEmployees') },
                  ...employeeOptionsByDepartment,
                ]}
                value={empId ? String(empId) : '0'}
                onValueChange={(v) => setEmpId(v ? Number(v) : 0)}
                placeholder={t('attendancePage.allEmployees')}
                searchPlaceholder={t('attendancePage.searchEmployee')}
                emptyMessage={t('attendancePage.emptyEmployee')}
              />
            </div>
          </div>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t('attendancePage.searchTable')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Button
                variant="default"
                disabled={!branchId || posting || loading}
                onClick={handlePostAttendance}
              >
                <Send className="w-4 h-4 mr-2" />
                {posting ? t('attendancePage.posting') : t('attendancePage.post')}
              </Button>
            </div>
          </div>
          <div className="rounded-md border overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-center">{t('attendancePage.colEmployeeCode')}</TableHead>
                  <TableHead className="text-center">{t('attendancePage.colEmployeeName')}</TableHead>
                  <TableHead className="text-center">{t('attendancePage.colAttendanceDate')}</TableHead>
                  <TableHead className="text-center">{t('attendancePage.colTimeIn')}</TableHead>
                  <TableHead className="text-center">{t('attendancePage.colTimeOut')}</TableHead>
                  <TableHead className="text-center">{t('attendancePage.colIsPresent')}</TableHead>
                  <TableHead className="text-center">{t('attendancePage.colWorkingHours')}</TableHead>
                  <TableHead className="text-center">{t('attendancePage.colLateHours')}</TableHead>
                  <TableHead className="text-center">{t('attendancePage.colIncompleteHours')}</TableHead>
                  <TableHead className="text-center w-[100px]">{t('attendancePage.colAction')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center text-muted-foreground py-8">
                      {!branchId
                        ? t('attendancePage.emptySelectBranch')
                        : loading
                          ? t('attendancePage.emptyLoading')
                          : rows.length === 0
                            ? t('attendancePage.emptyNoRecords')
                            : t('attendancePage.emptyNoMatch')}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredRows.map((row, index) => (
                    <TableRow key={row.id ?? index}>
                      <TableCell className="text-center">{row.employeeCode ?? '—'}</TableCell>
                      <TableCell className="text-center">
                        {formatEmployeeDisplayName(
                          (row.empId
                            ? employeesList.find((e) => e.id === row.empId)
                            : employeesList.find((e) => e.empCode === row.employeeCode)) || {}
                        ) || row.employeeName || '—'}
                      </TableCell>
                      <TableCell className="text-center">{formatAttendanceDate(row.attendanceDate, locale)}</TableCell>
                      <TableCell className="text-center">
                        {row.isPosted ? (
                          formatTimeDisplay(row.timeIn, locale)
                        ) : (
                          <input
                            type="time"
                            value={getRowTimeIn(row)}
                            onChange={(e) => setTimeIn(row.id, e.target.value)}
                            className="h-9 w-28 rounded border border-input bg-background px-2 text-center text-sm"
                          />
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {row.isPosted ? (
                          formatTimeDisplay(row.timeOut, locale)
                        ) : (
                          <input
                            type="time"
                            value={getRowTimeOut(row)}
                            onChange={(e) => setTimeOut(row.id, e.target.value)}
                            className="h-9 w-28 rounded border border-input bg-background px-2 text-center text-sm"
                          />
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {row.isPosted ? (
                          getRowIsPresent(row) === 1 ? t('attendancePage.present') : t('attendancePage.absent')
                        ) : (
                          <Select
                            value={String(getRowIsPresent(row))}
                            onValueChange={(v) => setIsPresent(row.id, Number(v))}
                          >
                            <SelectTrigger className="h-9 w-28 mx-auto">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="1">{t('attendancePage.present')}</SelectItem>
                              <SelectItem value="0">{t('attendancePage.absent')}</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      </TableCell>
                      <TableCell className="text-center">{getRowMetrics(row).totalAttendedHours}</TableCell>
                      <TableCell className="text-center">{getRowMetrics(row).lateHoursCount}</TableCell>
                      <TableCell className="text-center">{getRowMetrics(row).incompleteHours}</TableCell>
                      <TableCell className="text-center">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={row.isPosted || updatingId === row.id}
                          onClick={() => handleUpdateRow(row)}
                        >
                          {updatingId === row.id ? t('attendancePage.updatingShort') : t('attendancePage.update')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          {/* Pagination */}
          {(totalRecords > 0 || pageNumber > 1) && (
            <div className="flex flex-wrap items-center justify-between gap-4 mt-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span>
                  {totalRecords === 0
                    ? t('attendancePage.paginationNoRecords')
                    : t('attendancePage.paginationShowingRange', {
                        from: (pageNumber - 1) * pageSize + 1,
                        to: Math.min(pageNumber * pageSize, totalRecords),
                        total: totalRecords,
                      })}
                </span>
                <Select
                  value={String(pageSize)}
                  onValueChange={(v) => {
                    setPageSize(Number(v))
                    setPageNumber(1)
                  }}
                >
                  <SelectTrigger className="w-[90px]">
                    <SelectValue placeholder={t('attendancePage.pageSizePlaceholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    {[10, 25, 50, 100].map((n) => (
                      <SelectItem key={n} value={String(n)}>
                        {n}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span>{t('attendancePage.perPage')}</span>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pageNumber <= 1 || loading}
                  onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
                >
                  <ChevronLeft className="h-4 w-4" />
                  {t('attendancePage.previous')}
                </Button>
                <span className="text-sm text-muted-foreground px-2">
                  {t('attendancePage.pageOf', { page: pageNumber, totalPages: Math.max(1, Math.ceil(totalRecords / pageSize)) })}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={
                    pageNumber >= Math.ceil(totalRecords / pageSize) || loading || totalRecords === 0
                  }
                  onClick={() => setPageNumber((p) => p + 1)}
                >
                  {t('attendancePage.next')}
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
